'use client';

import React from 'react';
import type { Cornerstone } from '@benzinga/content-manager';
import { Icon } from '@benzinga/core-ui';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { LearnSectionLabel } from './LearnSectionLabel';

const CARD = 'flex items-center justify-between gap-4 px-4 py-3.5';
const CHIP = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs text-white';
const TITLE = 'text-[0.9375rem] font-bold leading-snug text-white';

interface LearnNavItemProps {
  className?: string;
  icon: any;
  label: string;
  link: string;
  title: string;
  rel?: string;
}

export const LearnLessonNav: React.FC<{ syllabus: Cornerstone }> = ({ syllabus }) => {
  const lessons = Array.isArray(syllabus.child_posts) ? syllabus.child_posts : [];
  const activeIndex = lessons.findIndex(lesson => lesson.id === syllabus.current_post_id);
  if (activeIndex < 0) return null;

  const previous = activeIndex > 0 ? lessons[activeIndex - 1] : null;
  const next = activeIndex < lessons.length - 1 ? lessons[activeIndex + 1] : null;
  if (!previous && !next) return null;

  return (
    <nav aria-label="Lesson navigation" className="mt-6">
      <LearnSectionLabel className="mb-3" label="Continue Your Journey" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {previous && (
          <LearnNavItem
            className={`rounded-[10px] border border-emerald-800 bg-emerald-700 hover:bg-emerald-600`}
            icon={faCheck}
            label={'Previous Lesson'}
            link={previous.link}
            rel="prev"
            title={previous.title}
          />
        )}
        {next && (
          <LearnNavItem
            className={`rounded-[10px] border border-white/10 bg-bzblue-1100 hover:bg-bzblue-1000`}
            icon={faChevronRight}
            label={'Up Next'}
            link={next.link}
            rel="next"
            title={next.title}
          />
        )}
      </div>
    </nav>
  );
};

const LearnNavItem: React.FC<LearnNavItemProps> = ({ className, link, title, rel, icon, label }) => {
  return (
    <a className={`${CARD} ${className}`} href={link} rel={rel}>
      <span className="flex min-w-0 flex-col gap-0.5">
        <LearnSectionLabel className="!text-bzblue-500" label={label} />
        <span className={TITLE}>{title}</span>
      </span>
      <span className={`${CHIP} bg-bzblue-700`}>
        <Icon icon={icon} />
      </span>
    </a>
  );
};

export default LearnLessonNav;
